import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Emblem } from '../../components/Emblem';
import { Shirt } from '../../components/Shirt';
import { ProductThumb } from '../../components/ProductGallery';
import { PaczkomatPicker } from '../../components/PaczkomatPicker';
import { PhoneInput } from '../../components/PhoneInput';
import { useShop } from '../ShopState';
import { ProductSpec } from '../ProductSpec';
import { COLOR_META } from '../colors';
import { quotaExceededMessage } from '../quotaMessage';
import { createOrder, createStripeSession } from '../../api/orders';
import { ApiError } from '../../api/client';
import { formatZl } from '../../types';
import { groszeToZl } from '../../util/money';
import { parcelSizeForShirtCount, shippingCostGrosze } from '../../util/parcelSize';
import { SHIPPING_COUNTRIES } from '../../util/shippingCountries';

export function Checkout() {
  const navigate = useNavigate();
  const { cart, cartTotalQty, cartTotalGrosze, checkout, setCheckout, verification, setVerification, setLastOrder, catalog, markShopClosed } = useShop();

  useEffect(() => {
    if (cart.length === 0) navigate('/sklep', { replace: true });
  }, [cart.length, navigate]);

  if (catalog.status !== 'ready') {
    return <div style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>Wczytywanie…</div>;
  }
  if (cart.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>Koszyk jest pusty.</div>;
  }
  const { shopStatus, products } = catalog;
  const itemSubtotal = groszeToZl(cartTotalGrosze);
  // This order joins a parcel an earlier order for the same external order already paid for, so
  // there is no method to pick, no address to type and nothing to charge. The server decides this
  // again at creation; see shipping_plan_changed below.
  const ridesAlong = verification?.shippingMode === 'ride_along';
  // Foreign parcels are quoted per destination and booked with a carrier after the fact, so
  // checkout takes the shirts alone and collects a country instead of an address.
  const goesAbroad = !ridesAlong && checkout.method === 'zagranica';
  // Same rule the API charges by (apps/api/src/util/parcelSize.ts): the gabaryt follows the
  // shirt count, so the price moves as the cart grows.
  const parcelSize = parcelSizeForShirtCount(cartTotalQty);
  const shippingCost = ridesAlong
    ? 0
    : groszeToZl(shippingCostGrosze(shopStatus, checkout.method, parcelSize));
  const total = itemSubtotal + shippingCost;
  const shirtsLabel = cartTotalQty === 1 ? 'KOSZULKA' : cartTotalQty < 5 ? 'KOSZULKI' : 'KOSZULEK';

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const lastNameMissing = !checkout.lastName.trim();
  const emailMissing = !checkout.emailValid;
  const paczkomatMissing = !ridesAlong && checkout.method === 'paczkomat' && !checkout.paczkomat;
  const phoneMissing = !checkout.phoneValid;
  const countryMissing = goesAbroad && !checkout.country;
  // POST /orders requires street, postalCode and city to be non-empty for kurier, so without this
  // the button stays enabled and the only feedback is a 400 after submitting.
  const courierMissing =
    !ridesAlong &&
    checkout.method === 'kurier' &&
    (!checkout.courier ||
      !checkout.courier.street.trim() ||
      !checkout.courier.postalCode.trim() ||
      !checkout.courier.city.trim());
  const cannotSubmit =
    lastNameMissing ||
    emailMissing ||
    paczkomatMissing ||
    courierMissing ||
    countryMissing ||
    phoneMissing;
  const cannotSubmitReason = lastNameMissing
    ? 'Podaj nazwisko, aby kontynuować.'
    : emailMissing
      ? 'Podaj poprawny adres email, aby kontynuować.'
      : phoneMissing
        ? 'Podaj poprawny numer telefonu, aby kontynuować.'
        : paczkomatMissing
          ? 'Wybierz paczkomat, aby kontynuować.'
          : countryMissing
            ? 'Wybierz kraj docelowy, aby kontynuować.'
            : courierMissing
              ? 'Podaj ulicę, kod pocztowy i miasto, aby kontynuować.'
              : null;

  const submit = async () => {
    if (lastNameMissing) {
      setSubmitError('Podaj nazwisko.');
      return;
    }
    if (emailMissing) {
      setSubmitError('Podaj poprawny adres email.');
      return;
    }
    if (paczkomatMissing) {
      setSubmitError('Wybierz paczkomat z mapy lub wyszukiwarki.');
      return;
    }
    if (courierMissing) {
      setSubmitError('Podaj ulicę, kod pocztowy i miasto dla przesyłki kurierskiej.');
      return;
    }
    if (countryMissing) {
      setSubmitError('Wybierz kraj, do którego ma trafić paczka.');
      return;
    }
    if (phoneMissing) {
      setSubmitError('Podaj poprawny numer telefonu.');
      return;
    }
    if (!verification) {
      setSubmitError('Weryfikacja wygasła - podaj numer zamówienia jeszcze raz.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const customerName = [verification.firstName, checkout.lastName]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(' ');
      const created = await createOrder({
        customerName,
        email: checkout.email.trim(),
        phone: checkout.phone,
        externalOrderId: verification.externalOrderId,
        firstName: verification.firstName,
        items: cart.map((row) => ({
          product_id: row.productId,
          cut: row.cut,
          size: row.size,
          qty: row.qty,
        })),
        // A ride-along sends neither: the server takes both from the parcel it is joining, and a
        // method sent from here would just be ignored.
        shipping_method: ridesAlong ? undefined : checkout.method,
        shipping_address: ridesAlong
          ? {}
          : {
              paczkomat: checkout.method === 'paczkomat' ? checkout.paczkomat : undefined,
              courier: checkout.method === 'kurier' ? checkout.courier : undefined,
              international: goesAbroad ? { country: checkout.country } : undefined,
            },
        customer_comment_text: checkout.comment.trim() || undefined,
      });
      // Straight to Stripe. Stopping on a confirmation page first leaves the buyer on a
      // screen that reads like a receipt for an order nobody has paid for.
      try {
        const session = await createStripeSession(created.id, created.accessToken);
        window.location.assign(session.url);
        return;
      } catch (sessionErr) {
        console.error('[stripe] session failed at checkout', sessionErr);
      }
      // The session is reset on arrival, not here: clearing the gate while this route is still
      // mounted sends it straight back to the gate, because the router applies its own navigation
      // as a transition that an urgent state update outranks.
      navigate(`/sklep/sukces/${created.id}?t=${encodeURIComponent(created.accessToken)}`, {
        state: { placedOrderId: created.id },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        // The allowance is re-derived under a row lock at creation, so this can fire even though
        // the gate said there was room: another order against the same external order id got
        // there first. Report the count the server just computed, not the stale one in session.
        if (err.message === 'quota_exceeded') {
          setSubmitError(quotaExceededMessage(err.body));
        } else if (err.message === 'shop_closed') {
          markShopClosed();
        } else if (err.message === 'external_order_not_found') {
          setSubmitError('Nie rozpoznajemy tego zamówienia - zweryfikuj je ponownie.');
        } else if (err.message === 'shipping_plan_changed' || err.message === 'shipping_closed') {
          // Another order for the same external order landed or was cancelled while this one was
          // being filled in, so the delivery step on screen is not the one the shop would apply.
          // Re-running the gate is what redraws it.
          setVerification(null);
          setSubmitError('Wysyłka tego zamówienia się zmieniła - zweryfikuj zamówienie jeszcze raz.');
        } else if (err.message === 'invalid_paczkomat') {
          setCheckout({ paczkomat: undefined });
          setSubmitError('Ten paczkomat nie jest dostępny - wybierz inny.');
        } else if (err.message === 'paczkomat_validation_unavailable') {
          setSubmitError('InPost chwilowo niedostępny - spróbuj za chwilę.');
        } else if (err.message === 'stripe_unavailable') {
          setSubmitError(
            'Płatności są chwilowo niedostępne - nie możemy teraz przyjąć zamówienia. Spróbuj za chwilę.',
          );
        } else if (err.message === 'invalid_phone') {
          setSubmitError('Numer telefonu jest nieprawidłowy.');
        } else if (err.message === 'missing_country' || err.message === 'invalid_country') {
          setSubmitError('Wybierz kraj z listy, aby wysłać zamówienie za granicę.');
        } else {
          setSubmitError('Nie udało się złożyć zamówienia.');
        }
      } else {
        setSubmitError('Nie udało się złożyć zamówienia.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="phone-screen">
      <div
        style={{
          position: 'relative',
          zIndex: 5,
          padding: '12px 18px 6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mono"
          style={{
            background: 'transparent',
            border: 0,
            color: 'var(--shop-fg)',
            fontSize: 11,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ← WRÓĆ
        </button>
        <Link to="/" style={{ display: 'flex', color: 'inherit', textDecoration: 'none' }}>
          <Emblem size={36} />
        </Link>
        <span className="mono" style={{ fontSize: 11, color: 'var(--orange)' }}>
          {cartTotalQty} {shirtsLabel}
        </span>
      </div>

      <div className="scroll" style={{ height: 'calc(100% - 90px)', padding: '8px 22px 24px' }}>
        <div className="display" style={{ fontSize: 30, marginTop: 6 }}>
          ZAMÓWIENIE
        </div>

        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cart.map((row) => {
            const meta = COLOR_META[row.color];
            const product = products.find((p) => p.id === row.productId);
            const rowSubtotal = groszeToZl(row.unitPriceGrosze * row.qty);
            return (
              <div
                key={row.id}
                style={{
                  padding: 12,
                  border: '2px solid var(--shop-fg)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 117,
                    height: 117,
                    background: 'var(--shop-card-bg)',
                    border: '1px solid var(--shop-fg)',
                    flexShrink: 0,
                  }}
                >
                  <ProductThumb
                    images={product?.images ?? []}
                    alt={product?.name ?? 'Koszulka'}
                    fallback={<Shirt color={row.color} cut={row.cut} />}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="display" style={{ fontSize: 14 }}>
                    KOSZULKA · {meta.label}
                  </div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'rgba(var(--shop-muted-rgb),.6)', letterSpacing: '.08em' }}>
                    {row.cut.toUpperCase()} · {row.size} · {row.qty} SZT
                  </div>
                  <ProductSpec product={product} fontSize={10} />
                </div>
                <div className="display" style={{ fontSize: 14, color: 'var(--orange)', flexShrink: 0 }}>
                  {rowSubtotal} ZŁ
                </div>
              </div>
            );
          })}
        </div>

        <SectionHeader>① ZAMÓWIENIE</SectionHeader>
        <div
          style={{
            padding: 12,
            border: '2px solid var(--shop-fg)',
            background: 'rgba(var(--shop-muted-rgb),.04)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="mono" style={{ fontSize: 11, color: 'var(--shop-fg)', letterSpacing: '.14em' }}>
            ZWERYFIKOWANO
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '6px 8px',
              background: 'transparent',
              border: '1px solid rgba(var(--shop-muted-rgb),.3)',
              color: 'var(--shop-fg)',
              fontFamily: 'var(--display)',
              fontSize: 10,
              letterSpacing: '.1em',
              cursor: 'pointer',
            }}
          >
            ZMIEŃ
          </button>
        </div>

        <SectionHeader>② KONTAKT</SectionHeader>
        <div className="field">
          <label htmlFor="lastname">Nazwisko</label>
          <input
            id="lastname"
            className="input"
            value={checkout.lastName}
            onChange={(e) => setCheckout({ lastName: e.target.value })}
            autoComplete="family-name"
          />
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={checkout.email}
            onChange={(e) => {
              const email = e.target.value;
              setCheckout({
                email,
                emailValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
              });
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="phone">Telefon</label>
          <PhoneInput
            id="phone"
            variant="shop"
            scope={goesAbroad ? 'world' : 'inpost'}
            value={checkout.phone}
            onChange={(phone, phoneValid) => setCheckout({ phone, phoneValid })}
          />
        </div>

        <SectionHeader>③ WYSYŁKA</SectionHeader>
        {ridesAlong ? (
          <div
            style={{
              padding: 14,
              border: '2px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
            }}
          >
            <div
              className="display"
              style={{ fontSize: 14, letterSpacing: '.08em', color: 'var(--orange)' }}
            >
              DOŁĄCZAMY DO TWOJEJ PACZKI
            </div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Te koszulki spakujemy razem z Twoim wcześniejszym zamówieniem i wyślemy pod ten sam
              adres. Nie płacisz za wysyłkę drugi raz.
            </div>
          </div>
        ) : (
          <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(
            [
              {
                id: 'paczkomat' as const,
                name: 'PACZKOMAT INPOST',
                price: formatZl(groszeToZl(shippingCostGrosze(shopStatus, 'paczkomat', parcelSize))),
              },
              {
                id: 'kurier' as const,
                name: 'KURIER INPOST',
                price: formatZl(groszeToZl(shippingCostGrosze(shopStatus, 'kurier', parcelSize))),
              },
              {
                id: 'zagranica' as const,
                name: 'WYSYŁKA ZAGRANICZNA',
                price: 'WYCENA INDYWIDUALNA',
              },
            ]
          ).map((m) => {
            const active = checkout.method === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setCheckout({ method: m.id })}
                style={{
                  textAlign: 'left',
                  padding: 12,
                  cursor: 'pointer',
                  border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                  background: active ? 'rgba(255,107,26,.1)' : 'transparent',
                  color: 'var(--shop-fg)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span className="display" style={{ fontSize: 14, color: active ? 'var(--orange)' : 'var(--shop-fg)' }}>
                  {m.name}
                </span>
                <span className="mono" style={{ fontSize: 10.5, color: active ? 'var(--orange)' : 'var(--shop-fg)' }}>
                  {m.price.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>

        {checkout.method === 'paczkomat' && (
          <div style={{ marginTop: 8 }}>
            <PaczkomatPicker
              value={checkout.paczkomat ?? null}
              onChange={(next) => setCheckout({ paczkomat: next ?? undefined })}
            />
          </div>
        )}
        {checkout.method === 'kurier' && (
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            <input
              className="input"
              placeholder="Ulica i numer"
              value={checkout.courier?.street ?? ''}
              onChange={(e) =>
                setCheckout({
                  courier: {
                    street: e.target.value,
                    postalCode: checkout.courier?.postalCode ?? '',
                    city: checkout.courier?.city ?? '',
                  },
                })
              }
            />
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <input
                className="input"
                placeholder="00-000"
                value={checkout.courier?.postalCode ?? ''}
                onChange={(e) =>
                  setCheckout({
                    courier: {
                      street: checkout.courier?.street ?? '',
                      postalCode: e.target.value,
                      city: checkout.courier?.city ?? '',
                    },
                  })
                }
              />
              <input
                className="input"
                placeholder="Miasto"
                value={checkout.courier?.city ?? ''}
                onChange={(e) =>
                  setCheckout({
                    courier: {
                      street: checkout.courier?.street ?? '',
                      postalCode: checkout.courier?.postalCode ?? '',
                      city: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        )}
        {checkout.method === 'zagranica' && (
          <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            <div
              style={{
                padding: 12,
                border: '2px solid var(--orange)',
                background: 'rgba(255,107,26,.08)',
              }}
            >
              <div
                className="display"
                style={{ fontSize: 13, letterSpacing: '.08em', color: 'var(--orange)' }}
              >
                WYSYŁKĘ USTALAMY INDYWIDUALNIE
              </div>
              <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
                Teraz płacisz tylko za koszulki. Po zaksięgowaniu wpłaty napiszemy do Ciebie, żeby
                ustalić sposób i koszt wysyłki do wybranego kraju.
              </div>
            </div>
            <div className="field">
              <label htmlFor="m-country">Kraj docelowy</label>
              <select
                id="m-country"
                className="input"
                value={checkout.country}
                onChange={(e) => setCheckout({ country: e.target.value })}
              >
                <option value="">— wybierz kraj —</option>
                {SHIPPING_COUNTRIES.map((country) => (
                  <option key={country.iso} value={country.iso}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="m-comment">Uwagi do wysyłki (opcjonalnie)</label>
              <textarea
                id="m-comment"
                className="input"
                rows={3}
                value={checkout.comment}
                onChange={(e) => setCheckout({ comment: e.target.value })}
                placeholder="Miasto, przewoźnik, termin — co pomoże nam wycenić paczkę."
              />
            </div>
          </div>
        )}
          </>
        )}

        <div
          style={{
            marginTop: 22,
            padding: 14,
            background: 'rgba(255,107,26,.08)',
            border: '2px solid var(--orange)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>Koszulki × {cartTotalQty}</span>
            <span className="mono">{formatZl(itemSubtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
            <span>
              {ridesAlong
                ? 'Wysyłka (dołączona do paczki)'
                : goesAbroad
                  ? 'Wysyłka zagraniczna'
                  : checkout.method === 'paczkomat'
                    ? 'Paczkomat InPost'
                    : 'Kurier InPost'}
            </span>
            <span className="mono">
              {goesAbroad
                ? 'USTALANA OSOBNO'
                : ridesAlong
                  ? '0,00 ZŁ'
                  : formatZl(shippingCost)}
            </span>
          </div>
          <div style={{ borderTop: '1px dashed rgba(var(--shop-muted-rgb),.3)', margin: '10px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="display" style={{ fontSize: 14 }}>
              RAZEM
            </span>
            <span className="display" style={{ fontSize: 22, color: 'var(--orange)' }}>
              {formatZl(total).toUpperCase()}
            </span>
          </div>
          {goesAbroad && (
            <div
              className="mono"
              style={{ fontSize: 10, marginTop: 8, lineHeight: 1.5, color: 'var(--shop-fg)' }}
            >
              KWOTA OBEJMUJE SAME KOSZULKI. KOSZT WYSYŁKI ZA GRANICĘ USTALIMY Z TOBĄ OSOBNO.
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 14,
          background: 'linear-gradient(to top, var(--shop-bg) 70%, transparent)',
        }}
      >
        {submitError ? (
          <div
            className="mono"
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              border: '1px dashed var(--orange)',
              color: 'var(--orange)',
              fontSize: 11,
            }}
          >
            {submitError}
          </div>
        ) : cannotSubmit && !submitting && cannotSubmitReason ? (
          <div
            className="mono"
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              border: '1px dashed rgba(var(--shop-muted-rgb),.4)',
              color: 'rgba(var(--shop-muted-rgb),.9)',
              fontSize: 11,
            }}
          >
            {cannotSubmitReason}
          </div>
        ) : null}
        <button
          className="btn full"
          onClick={submit}
          disabled={submitting || cannotSubmit}
          style={{
            opacity: submitting || cannotSubmit ? 0.5 : 1,
            cursor: submitting || cannotSubmit ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'WYSYŁANIE…' : 'ZŁÓŻ ZAMÓWIENIE →'}
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div
      className="display"
      style={{
        fontSize: 12,
        letterSpacing: '.14em',
        marginTop: 22,
        marginBottom: 6,
        color: 'var(--orange)',
      }}
    >
      {children}
    </div>
  );
}
